import React, { useState, useRef, useEffect } from 'react';
import { useAudioRecording } from './hooks/useAudioRecording.js';

export const RecordingModal = ({ 
  isOpen, 
  onClose, 
  scrollText, 
  scrollTitle,
  userId, 
  scrollId,
  onSaveComplete 
}) => {
  const {
    isRecording,
    recordingBlob,
    recordingUrl,
    duration,
    error,
    startRecording,
    stopRecording,
    resetRecording,
    saveRecording
  } = useAudioRecording(userId, scrollId);

  const [isPlaying, setIsPlaying] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const audioRef = useRef(null);

  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.onended = () => setIsPlaying(false);
    }
  }, [recordingUrl]);

  const handlePlayPause = () => {
    if (!audioRef.current) return;
    if (isPlaying) {
      audioRef.current.pause();
      setIsPlaying(false);
    } else {
      audioRef.current.play();
      setIsPlaying(true);
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    const savedUrl = await saveRecording();
    setIsSaving(false);
    if (savedUrl) {
      onSaveComplete(savedUrl);
      onClose();
    }
  };

  const handleReRecord = () => {
    resetRecording();
    setIsPlaying(false);
  };

  if (!isOpen) return null;

  // ── RECORDING MODE: full screen scroll text with minimal UI ──
  if (isRecording) {
    return (
      <div style={styles.overlay}>
        <div style={styles.modal}>
          {/* Full screen scroll text */}
          <div style={styles.recordingScrollArea}>
            <h3 style={styles.scrollTitle}>{scrollTitle}</h3>
            <div style={styles.scrollText}>
              {scrollText}
            </div>
          </div>

          {/* Recording bar pinned to bottom */}
          <div style={styles.recordingBar}>
            <div style={styles.recordingIndicator}>
              <div style={styles.recordingDot}></div>
              <span style={styles.recordingLabel}>Recording</span>
            </div>
            <div style={styles.timer}>{duration}</div>
            <button onClick={stopRecording} style={styles.stopButton}>
              Stop
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── PREVIEW MODE: after recording is done ──
  if (recordingBlob) {
    return (
      <div style={styles.overlay}>
        <div style={styles.modal}>
          <div style={styles.header}>
            <h2 style={styles.headerTitle}>Recording Complete</h2>
            <button onClick={onClose} style={styles.closeButton}>x</button>
          </div>

          <div style={styles.content}>
            {/* Preview controls */}
            <div style={styles.previewState}>
              <div style={styles.completeIcon}>&#10003;</div>
              <p style={styles.completeTitle}>Great job!</p>
              <p style={styles.completeDuration}>Duration: {duration}</p>

              <audio ref={audioRef} src={recordingUrl} style={{ display: 'none' }} />

              <div style={styles.previewButtons}>
                <button onClick={handlePlayPause} style={styles.previewButton}>
                  {isPlaying ? 'Pause' : 'Preview'}
                </button>
                <button onClick={handleReRecord} style={styles.reRecordButtonSmall}>
                  Re-record
                </button>
              </div>
            </div>

            {/* Scroll text for reference */}
            <div style={styles.scrollTextContainer}>
              <h3 style={styles.scrollTitle}>{scrollTitle}</h3>
              <div style={styles.scrollText}>
                {scrollText}
              </div>
            </div>
          </div>

          <div style={styles.footer}>
            <button onClick={onClose} style={styles.cancelButton}>Cancel</button>
            <button onClick={handleSave} disabled={isSaving} style={styles.saveButton}>
              {isSaving ? 'Saving...' : 'Save Recording'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── DEFAULT MODE: ready to record ──
  return (
    <div style={styles.overlay}>
      <div style={styles.modal}>
        <div style={styles.header}>
          <h2 style={styles.headerTitle}>Record Your Voice</h2>
          <button onClick={onClose} style={styles.closeButton}>x</button>
        </div>

        <div style={styles.content}>
          {/* Instructions + record button at top */}
          <div style={styles.instructions}>
            <p style={styles.instructionText}>
              Read the scroll below out loud while recording. Tap Start Recording, then read at your own pace.
            </p>
            <button onClick={startRecording} style={styles.startButton}>
              Start Recording
            </button>
          </div>

          {error && (
            <div style={styles.error}>
              <p style={styles.errorText}>{error}</p>
            </div>
          )}

          {/* Scroll text takes up remaining space */}
          <div style={styles.scrollTextContainer}>
            <h3 style={styles.scrollTitle}>{scrollTitle}</h3>
            <div style={styles.scrollText}>
              {scrollText}
            </div>
          </div>
        </div>

        <div style={styles.footer}>
          <button onClick={onClose} style={styles.cancelButton}>Cancel</button>
        </div>
      </div>
    </div>
  );
};

const styles = {
  overlay: {
    position: 'fixed',
    inset: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.85)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    padding: '16px',
  },
  modal: {
    backgroundColor: '#1a1f2e',
    borderRadius: '12px',
    maxWidth: '800px',
    width: '100%',
    maxHeight: '90vh',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  header: {
    padding: '20px 24px',
    borderBottom: '1px solid #2a3142',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexShrink: 0,
  },
  headerTitle: {
    fontSize: '1.25rem',
    fontWeight: '600',
    color: '#e2e8f0',
    margin: 0,
  },
  closeButton: {
    background: 'none',
    border: 'none',
    color: '#94a3b8',
    fontSize: '1.25rem',
    cursor: 'pointer',
    padding: '0.2em 0.4em',
  },

  // Recording mode styles
  recordingBar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '12px 20px',
    backgroundColor: '#0f1419',
    borderBottom: '1px solid #2a3142',
    flexShrink: 0,
  },
  recordingIndicator: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  recordingDot: {
    width: '10px',
    height: '10px',
    backgroundColor: '#ef4444',
    borderRadius: '50%',
    animation: 'pulse 1s infinite',
  },
  recordingLabel: {
    fontSize: '0.875rem',
    fontWeight: '600',
    color: '#ef4444',
  },
  timer: {
    fontSize: '1.5rem',
    fontFamily: 'monospace',
    color: '#e2e8f0',
    fontWeight: '600',
  },
  stopButton: {
    padding: '0.5714em 1.4286em',
    backgroundColor: '#ef4444',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '0.875rem',
    fontWeight: '600',
    cursor: 'pointer',
  },
  recordingScrollArea: {
    flex: 1,
    overflowY: 'auto',
    padding: '24px',
  },

  // Default/preview content area
  content: {
    flex: 1,
    overflowY: 'auto',
    padding: '24px',
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
  },
  instructions: {
    padding: '16px 20px',
    backgroundColor: '#14b8a610',
    border: '1px solid #14b8a630',
    borderRadius: '10px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '16px',
    flexWrap: 'wrap',
    flexShrink: 0,
  },
  instructionText: {
    fontSize: '0.875rem',
    color: '#93c5fd',
    margin: 0,
    flex: 1,
    lineHeight: '1.5',
  },
  startButton: {
    padding: '0.8em 1.8667em',
    background: 'linear-gradient(135deg, #14b8a6 0%, #0ea5e9 100%)',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '0.9375rem',
    fontWeight: '700',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    flexShrink: 0,
  },
  scrollTextContainer: {
    flex: 1,
    padding: '20px',
    backgroundColor: '#0f1419',
    borderRadius: '8px',
    border: '1px solid #2a3142',
    overflowY: 'auto',
    minHeight: '200px',
  },
  scrollTitle: {
    fontSize: '1rem',
    fontWeight: '600',
    color: '#e2e8f0',
    marginBottom: '12px',
    marginTop: 0,
  },
  scrollText: {
    fontSize: '0.9375rem',
    lineHeight: '1.8',
    color: '#cbd5e1',
    whiteSpace: 'pre-wrap',
  },
  error: {
    padding: '12px 16px',
    backgroundColor: '#ef444415',
    border: '1px solid #ef4444',
    borderRadius: '8px',
    flexShrink: 0,
  },
  errorText: {
    fontSize: '0.875rem',
    color: '#f87171',
    margin: 0,
  },

  // Preview state
  previewState: {
    textAlign: 'center',
    padding: '16px',
    backgroundColor: '#0f1419',
    borderRadius: '10px',
    border: '1px solid #2a3142',
    flexShrink: 0,
  },
  completeIcon: {
    fontSize: '2.5rem',
    color: '#10b981',
  },
  completeTitle: {
    fontSize: '1.125rem',
    fontWeight: '600',
    color: '#e2e8f0',
    margin: '8px 0 4px',
  },
  completeDuration: {
    fontSize: '0.8125rem',
    color: '#94a3b8',
    marginBottom: '16px',
  },
  previewButtons: {
    display: 'flex',
    gap: '12px',
    justifyContent: 'center',
  },
  previewButton: {
    padding: '0.7143em 1.7143em',
    backgroundColor: '#10b981',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '0.875rem',
    fontWeight: '600',
    cursor: 'pointer',
  },
  reRecordButtonSmall: {
    padding: '0.7143em 1.7143em',
    backgroundColor: '#475569',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '0.875rem',
    fontWeight: '600',
    cursor: 'pointer',
  },

  // Footer
  footer: {
    padding: '16px 24px',
    borderTop: '1px solid #2a3142',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexShrink: 0,
  },
  cancelButton: {
    padding: '0.5714em 1.1429em',
    color: '#94a3b8',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    fontSize: '0.875rem',
  },
  saveButton: {
    padding: '0.8571em 2em',
    background: 'linear-gradient(135deg, #14b8a6 0%, #0ea5e9 100%)',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '0.875rem',
    fontWeight: '700',
    cursor: 'pointer',
  },
};